import React from 'react';
import ReactGA from 'react-ga4';
import { connect, Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { ConnectedRouter } from 'connected-react-router';
import { Route, Switch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReduxToastr from 'react-redux-toastr';
import 'react-dates/initialize';
import * as Sentry from '@sentry/browser';
import { PostHogProvider } from 'posthog-js/react';
// CSS
import 'react-redux-toastr/lib/css/react-redux-toastr.min.css';
import 'react-dates/lib/css/_datepicker.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import { history, persistor, store } from './redux/store';
import { getSiteSettings } from './redux/actions/settings';
import ForgotPassword from './containers/Login/ForgotPassword';
import PasswordReset from './containers/Login/PasswordReset';
import PrivateRoute from './components/PrivateRoute';
import './App.scss';
import * as constants from './utils/constants';
import ErrorBoundary from './components/ErrorBoundary';
import { defaults } from 'react-chartjs-2';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// This sets the default font for all charts to be our custom font.
defaults.global.defaultFontFamily = 'campton';

if (process.env.NODE_ENV === 'production') {
  ReactGA.initialize('G-84SK96EXBM');
}

const loading = () => (
  <div className="animated fadeIn pt-3 text-center">Loading...</div>
);

const Login = React.lazy(() => import('./containers/Login'));
const Signup = React.lazy(() => import('./containers/Signup'));
const Layout = React.lazy(() => import('./containers/Layout'));

const AeLogin = React.lazy(() => import('containers/Ae/AeLogin'));
const AeOrganizations = React.lazy(
  () => import('containers/Ae/AeOrganizations'),
);

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
  });
}

const isMainSite = process.env.REACT_APP_ENVIRONMENT === 'default';

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 12 * MS_PER_HOUR,
      refetchInterval: 12 * MS_PER_HOUR,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorBoundary>
          <PostHogProviderWrapper>
            <PublicRoutesContainer history={history} />
          </PostHogProviderWrapper>
        </ErrorBoundary>
      </PersistGate>
    </Provider>
    <ReactQueryDevtools />
  </QueryClientProvider>
);

/**
 * Wrapper for post hog provider so that it only captures in production.
 * @param {*} param0
 * @returns
 */
const PostHogProviderWrapper = ({ children }) => {
  if (process.env.NODE_ENV !== 'production') return <>{children}</>;
  return (
    <PostHogProvider
      apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
      }}
    >
      {children}
    </PostHogProvider>
  );
};

class PublicRoutes extends React.Component {
  UNSAFE_componentWillMount() {
    this.props.getSiteSettings();
  }

  render() {
    const {
      history,
      isLoading,
      faviconUrl,
      siteTitle,
      showSignupPage,
      styles,
    } = this.props;

    if (isLoading) return null;

    return (
      // TODO check history, location, match value is rendered correctly in HTML
      <ConnectedRouter history={history}>
        <React.Suspense fallback={loading()}>
          <Helmet>
            <link rel="shortcut icon" href={faviconUrl} type="image/x-icon" />
            <title>{siteTitle}</title>
            <style>
              {`
                .app-header {
                  background-color: ${styles.topBarBackgroundColor};
                }
                .sidebar {
                  background: ${styles.sideBarBackgroundColor};
                }
                `}
            </style>
          </Helmet>
          <Switch>
            <Route exact path="/login/" component={Login} />

            {showSignupPage && (
              <Route exact path="/signup/" component={Signup} />
            )}

            {process.env.REACT_APP_DISABLE_FORGOT_PASSWORD ? null : (
              <Route
                exact
                path="/forgot-password/"
                component={ForgotPassword}
              />
            )}

            <Route exact path="/password-reset" component={PasswordReset} />
            {isMainSite && (
              <Route exact path="/ae/login/" component={AeLogin} />
            )}
            {isMainSite && (
              <PrivateRoute
                exact
                path="/ae/organizations/"
                component={AeOrganizations}
                unauthorizedPathname="/ae/login"
              />
            )}

            <PrivateRoute path="/" component={Layout} />
          </Switch>
          <ReduxToastr
            timeOut={10_000}
            transitionIn="fadeIn"
            transitionOut="fadeOut"
            closeOnToastrClick
          />
        </React.Suspense>
      </ConnectedRouter>
    );
  }
}

const mapStateToProps = (state) => ({
  faviconUrl: state.settings.faviconUrl || constants.DEFAULT_FAVICON_URL,
  siteTitle: state.settings.siteTitle || constants.DEFAULT_SITE_TITLE,
  isLoading: state.settings.isFetching,
  showSignupPage: state.auth.user?.profile?.is_amz_ae
    ? false
    : state.settings.showSignupPage,
  styles: state.settings.styles,
});

const mapDispatchToProps = {
  getSiteSettings,
};

const PublicRoutesContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PublicRoutes);

export default App;
